import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'

import { DownloadCta } from './DownloadCta'
import { DownloadIcon, LogOutIcon, RefreshIcon, ShieldIcon, UserIcon } from './icons'
import { ProfileAvatar } from './ProfileAvatar'
import { NotificationBell } from '../notifications/NotificationBell'
import { isBrowser } from '../lib/appEnv'
import { requestUpdateCheck, updatesSupported } from '../lib/appUpdate'
import { logout } from '../lib/auth'
import { publish } from '../lib/tabSync'
import { useSession } from '../session/SessionProvider'

/**
 * Ust barin sag tarafi: bildirim, yonetim baglantisi, profil menusu.
 *
 * ## Neden ayri bir bilesen
 *
 * Iki ekran ayni ust bari gosteriyor: sohbet ve paylasilan sohbet. Giris
 * yapmis bir kullanici bir paylasim baglantisini actiginda kendi ust
 * barini goruyor - kullanici paylasilan ekranin "kendi sohbet ekraniyla
 * tamamen ayni" olmasini istedi. Bunu `ChatScreen`in icinde birakmak,
 * paylasilan gorunumde eksik kalmasi demekti.
 */
export function UserHeaderActions() {
  const { t } = useTranslation()
  const { state } = useSession()
  const isAdmin = state?.user?.role === 'admin' || state?.user?.role === 'superadmin'

  const signOut = useCallback(async () => {
    await logout().catch(() => undefined)
    // Diger sekmelere HABER VER: onlar da okuyucularini biraksin. Aksi halde
    // baska bir sekme olu bir oturumla akitmaya devam ediyor ve 401 alana
    // kadar bunu bilmiyor.
    publish({ type: 'logged_out' })
  }, [])

  return (
    <>
      <span className="header-actions">
        {/* Ust bardaki "Ara" dugmesi kullanici istegiyle KALDIRILDI: sohbet
            aramasi zaten kenar cubugunda. Komut paleti Ctrl+K ile acilmaya
            devam ediyor. */}
        {/* Bu sisteme uygun indirme - YALNIZCA tarayicida ve eslesme varsa
            gorunur (uygulamadan girildiginde gizli). */}
        <DownloadCta variant="inline" />
        <NotificationBell />
      </span>

      {/*
        Yonetim baglantisi YALNIZCA yoneticiye. Bu bir guvenlik onlemi
        DEGIL - sunucu zaten 403 donuyor; duz kullaniciya asla
        acamayacagi bir baglanti gostermemek icin.
      */}
      {isAdmin && (
        <Link to="/admin" className="btn-admin">
          <ShieldIcon size={15} />
          <span>{t('chat.header.admin')}</span>
        </Link>
      )}

      <span className="header-divider" aria-hidden="true" />

      <ProfileMenu
        name={state?.user?.name}
        email={state?.user?.email}
        avatarId={state?.user?.avatar_attachment_id}
        onSignOut={signOut}
      />
    </>
  )
}

/**
 * Cikis butonu.
 *
 * Cikistan sonra `refresh()` cagriliyor ve guard kullaniciyi `/login`e
 * atiyor - elle yonlendirme yapmiyoruz, cunku iki yerde karar vermek
 * ikisinin ayrisma riskini dogurur.
 */
function ProfileMenu({
  name,
  email,
  avatarId,
  onSignOut,
}: {
  name?: string
  email?: string
  avatarId?: string | null
  onSignOut(): Promise<void>
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const menu = useRef<HTMLDivElement>(null)
  const initial = (name || email || '?').trim().slice(0, 1).toUpperCase()

  useEffect(() => {
    function close(event: MouseEvent) {
      if (menu.current && !menu.current.contains(event.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  return (
    <div className="profile-menu" ref={menu}>
      <button
        type="button"
        className="profile-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('chat.header.profileMenu')}
      >
        <ProfileAvatar avatarId={avatarId} initial={initial} className="profile-trigger-avatar" />
      </button>
      {open && (
        <div className="profile-popover" role="menu">
          <div className="profile-popover-identity">
            <ProfileAvatar
              avatarId={avatarId}
              initial={initial}
              className="profile-popover-avatar"
            />
            <span className="profile-popover-meta">
              <strong>{name || email}</strong>
              {name && email && <small>{email}</small>}
            </span>
          </div>
          <div className="profile-popover-actions">
            <Link
              to="/settings"
              className="profile-menu-item"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <UserIcon size={18} />
              {t('chat.header.profileSettings')}
            </Link>
            <Link
              to="/settings/security"
              className="profile-menu-item"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <ShieldIcon size={18} />
              {t('security.title')}
            </Link>
            {isBrowser() && (
              <Link
                to="/settings#downloads"
                className="profile-menu-item"
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                <DownloadIcon size={18} />
                {t('settings.downloads.heading')}
              </Link>
            )}
            {updatesSupported() && (
              <button
                type="button"
                className="profile-menu-item"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  requestUpdateCheck()
                }}
              >
                <RefreshIcon size={18} />
                {t('appUpdate.check')}
              </button>
            )}
            <div className="profile-menu-divider" role="separator" />
            <SignOutButton
              onSignOut={onSignOut}
              className="profile-menu-item profile-menu-signout"
            />
          </div>
        </div>
      )}
    </div>
  )
}

function SignOutButton({
  onSignOut,
  className = 'btn-quiet',
}: {
  onSignOut(): Promise<void>
  className?: string
}) {
  const { t } = useTranslation()
  const { refresh } = useSession()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)

  return (
    <button
      type="button"
      className={className}
      disabled={busy}
      onClick={async () => {
        setBusy(true)
        try {
          await onSignOut()
        } finally {
          await refresh()
          navigate('/login', { replace: true })
        }
      }}
    >
      {className.includes('profile-menu-signout') && <LogOutIcon size={18} />}
      {t('chat.header.signOut')}
    </button>
  )
}
